/**
 * Unified RevenueCat purchases abstraction.
 *
 * Routes to @revenuecat/purchases-capacitor on native iOS/Android and to
 * @revenuecat/purchases-js (Web Billing) on the PWA. The rest of the app
 * imports getPurchases() and never touches the platform SDKs directly.
 *
 * The backend verify_entitlement remains the source of truth — this module
 * only drives the client-side purchase/trial flow.
 */

import { Capacitor } from "@capacitor/core";

/** RevenueCat entitlement identifier (lookup key) unlocked by the subscription. */
export const PREMIUM_ENTITLEMENT_ID = "Batch-Cooking Pro";

export type BillingPeriodUnit = "day" | "week" | "month" | "year";

export interface BillingPeriod {
  unit: BillingPeriodUnit;
  count: number;
}

export interface NormalizedPackage {
  identifier: string;
  /** Store-localised price for one billing period (e.g. "7,99 €"). */
  priceString: string | null;
  /** Numeric price in currency units (7.99), for per-month maths. */
  price: number | null;
  /** ISO 4217 currency code, when the store reports one. */
  currencyCode: string | null;
  /** Billing period of the subscription, when known. */
  period: BillingPeriod | null;
  /** Free-trial length offered by the store for this package, if any. */
  freeTrial: BillingPeriod | null;
  /** Opaque platform-specific package object, passed back to purchasePackage. */
  _raw: unknown;
}

export interface PurchasesService {
  /**
   * Make the SDK run as `appUserId` (the Supabase user id).
   *
   * The first call configures the SDK. Later calls for another user switch
   * identity on the live SDK (`Purchases.logIn` on native, `changeUser` on
   * web) — re-running `configure` on an already-configured native SDK would
   * keep the previous user's identity.
   */
  configure(apiKey: string, appUserId: string): Promise<void>;
  getAllPackages(): Promise<NormalizedPackage[]>;
  getDefaultPackage(): Promise<NormalizedPackage | null>;
  purchasePackage(pkg: NormalizedPackage): Promise<void>;
  /** Re-sync store receipts. Resolves true when the premium entitlement is active afterwards. */
  restorePurchases(): Promise<boolean>;
  logOut(): Promise<void>;
}

// ── Normalisation helpers ────────────────────────────────────────────────────

const UNIT_BY_LETTER: Record<string, BillingPeriodUnit> = {
  D: "day",
  W: "week",
  M: "month",
  Y: "year",
};

/** Parse an ISO-8601 duration such as "P1M", "P14D", "P1Y". */
export function parseIsoPeriod(
  iso: string | null | undefined,
): BillingPeriod | null {
  if (!iso) return null;
  const m = /^P(\d+)([DWMY])$/.exec(iso.trim().toUpperCase());
  if (!m) return null;
  const unit = UNIT_BY_LETTER[m[2] as string];
  return unit ? { unit, count: Number(m[1]) } : null;
}

function normaliseUnit(unit: unknown): BillingPeriodUnit | null {
  const u = String(unit ?? "").toLowerCase();
  return u === "day" || u === "week" || u === "month" || u === "year"
    ? u
    : null;
}

type Dict = Record<string, unknown>;
const asDict = (v: unknown): Dict | null =>
  v !== null && typeof v === "object" ? (v as Dict) : null;

/** Normalise a @revenuecat/purchases-js (Web Billing) Package. */
export function normalizeWebPackage(pkg: unknown): NormalizedPackage {
  const p = asDict(pkg) ?? {};
  const product = asDict(p.webBillingProduct) ?? asDict(p.rcBillingProduct);
  const price = asDict(product?.currentPrice) ?? asDict(product?.price);
  const micros =
    typeof price?.amountMicros === "number" ? price.amountMicros : null;
  const periodObj = asDict(product?.period);
  const periodUnit = normaliseUnit(periodObj?.unit);
  const period: BillingPeriod | null =
    periodUnit && typeof periodObj?.number === "number"
      ? { unit: periodUnit, count: periodObj.number }
      : parseIsoPeriod(product?.normalPeriodDuration as string | null);
  const trialPhase = asDict(product?.freeTrialPhase);
  const trialPeriodObj = asDict(trialPhase?.period);
  const trialUnit = normaliseUnit(trialPeriodObj?.unit);
  const freeTrial: BillingPeriod | null = trialPhase
    ? trialUnit && typeof trialPeriodObj?.number === "number"
      ? { unit: trialUnit, count: trialPeriodObj.number }
      : parseIsoPeriod(trialPhase.periodDuration as string | null)
    : null;
  return {
    identifier: String(p.identifier ?? ""),
    priceString:
      typeof price?.formattedPrice === "string" ? price.formattedPrice : null,
    price: micros !== null ? micros / 1_000_000 : null,
    currencyCode: typeof price?.currency === "string" ? price.currency : null,
    period,
    freeTrial,
    _raw: pkg,
  };
}

/** Normalise a @revenuecat/purchases-capacitor PurchasesPackage (iOS / Android). */
export function normalizeNativePackage(pkg: unknown): NormalizedPackage {
  const p = asDict(pkg) ?? {};
  const product = asDict(p.product) ?? {};
  // iOS: a free trial is an introductory price of 0.
  const intro = asDict(product.introPrice);
  let freeTrial: BillingPeriod | null = null;
  if (intro && intro.price === 0) {
    const unit = normaliseUnit(intro.periodUnit);
    if (unit && typeof intro.periodNumberOfUnits === "number") {
      freeTrial = { unit, count: intro.periodNumberOfUnits };
    } else {
      freeTrial = parseIsoPeriod(intro.period as string | null);
    }
  }
  // Android: the default subscription option carries a freePhase.
  if (!freeTrial) {
    const freePhase = asDict(asDict(product.defaultOption)?.freePhase);
    const bp = asDict(freePhase?.billingPeriod);
    if (bp) {
      const unit = normaliseUnit(bp.unit);
      freeTrial =
        unit && typeof bp.value === "number"
          ? { unit, count: bp.value }
          : parseIsoPeriod(bp.iso8601 as string | null);
    }
  }
  return {
    identifier: String(p.identifier ?? ""),
    priceString:
      typeof product.priceString === "string" ? product.priceString : null,
    price: typeof product.price === "number" ? product.price : null,
    currencyCode:
      typeof product.currencyCode === "string" ? product.currencyCode : null,
    period: parseIsoPeriod(product.subscriptionPeriod as string | null),
    freeTrial,
    _raw: pkg,
  };
}

/**
 * Whether an SDK error means "the user closed / cancelled the purchase".
 * purchases-js: `errorCode === ErrorCode.UserCancelledError (1)`.
 * purchases-capacitor: `code === "1"` (PURCHASE_CANCELLED_ERROR) or the
 * deprecated `userCancelled: true`.
 */
export function isUserCancelledError(err: unknown): boolean {
  const e = asDict(err);
  if (!e) return false;
  if (e.userCancelled === true) return true;
  if (e.errorCode === 1 || e.errorCode === "1") return true;
  return e.code === "1" || e.code === 1;
}

// ── Capacitor (native iOS / Android) ─────────────────────────────────────────

const nativeImpl: PurchasesService = {
  async configure(apiKey, appUserId) {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const configured = await Purchases.isConfigured();
    if (!configured?.isConfigured) {
      await Purchases.configure({ apiKey, appUserID: appUserId });
      return;
    }
    // Already configured (earlier user, or anonymous after logOut): switch
    // identity so purchases are attributed to the current Supabase user.
    const current = await Purchases.getAppUserID();
    if (current?.appUserID !== appUserId) {
      await Purchases.logIn({ appUserID: appUserId });
    }
  },

  async getAllPackages() {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const offerings = await Purchases.getOfferings();
    return (offerings.current?.availablePackages ?? []).map(
      normalizeNativePackage,
    );
  },

  async getDefaultPackage() {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    // getOfferings() returns PurchasesOfferings directly (not wrapped in { offerings }).
    const offerings = await Purchases.getOfferings();
    const pkg = offerings.current?.availablePackages?.[0];
    return pkg ? normalizeNativePackage(pkg) : null;
  },

  async purchasePackage(pkg) {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await Purchases.purchasePackage({ aPackage: pkg._raw as any });
  },

  async restorePurchases() {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const { customerInfo } = await Purchases.restorePurchases();
    return Boolean(customerInfo.entitlements.active[PREMIUM_ENTITLEMENT_ID]);
  },

  async logOut() {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    await Purchases.logOut();
  },
};

// ── Web Billing (@revenuecat/purchases-js) ────────────────────────────────────

// Module-level singleton — configure() assigns it, everything else reads it.
let _webInstance: import("@revenuecat/purchases-js").Purchases | null = null;

const webImpl: PurchasesService = {
  async configure(apiKey, appUserId) {
    if (_webInstance) {
      if (_webInstance.getAppUserId() !== appUserId) {
        await _webInstance.changeUser(appUserId);
      }
      return;
    }
    const { Purchases } = await import("@revenuecat/purchases-js");
    _webInstance = await Purchases.configure(apiKey, appUserId);
  },

  async getAllPackages() {
    if (!_webInstance) return [];
    const offerings = await _webInstance.getOfferings();
    return (offerings.current?.availablePackages ?? []).map(
      normalizeWebPackage,
    );
  },

  async getDefaultPackage() {
    if (!_webInstance) return null;
    const offerings = await _webInstance.getOfferings();
    const pkg = offerings.current?.availablePackages?.[0];
    return pkg ? normalizeWebPackage(pkg) : null;
  },

  async purchasePackage(pkg) {
    if (!_webInstance) throw new Error("RevenueCat not configured");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await _webInstance.purchasePackage(pkg._raw as any);
  },

  // Web Billing purchases are tied to the app user id: "restoring" means
  // re-reading the customer info for the signed-in user.
  async restorePurchases() {
    if (!_webInstance) return false;
    const info = await _webInstance.getCustomerInfo();
    return Boolean(info.entitlements.active[PREMIUM_ENTITLEMENT_ID]);
  },

  async logOut() {
    if (!_webInstance) return;
    // close() tears down the singleton; the next configure() call re-creates it.
    _webInstance.close();
    _webInstance = null;
  },
};

// ── Public entry point ────────────────────────────────────────────────────────

export function getPurchases(): PurchasesService {
  return Capacitor.isNativePlatform() ? nativeImpl : webImpl;
}
